import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { getStoredToken } from "./api";

/** Server-pushed event names. */
export const SOCKET_EVENTS = {
  event: "ap:event",
  runStarted: "ap:run-started",
  runFinished: "ap:run-finished",
  productUpdated: "ap:product-updated",
} as const;

let socket: Socket | null = null;

/**
 * Bumped whenever the socket is torn down and replaced. Mounted hooks watch it
 * so they rebind to the new connection instead of listening to a dead one.
 */
let generation = 0;
const generationListeners = new Set<() => void>();

function bumpGeneration(): void {
  generation += 1;
  generationListeners.forEach((notify) => notify());
}

function useGeneration(): number {
  const [value, setValue] = useState(generation);
  useEffect(() => {
    const notify = () => setValue(generation);
    generationListeners.add(notify);
    notify();
    return () => {
      generationListeners.delete(notify);
    };
  }, []);
  return value;
}

/**
 * The one socket for the whole app, created on first use.
 *
 * The backend may not be running — that is the normal state during a cold
 * start — so every failure path here is silent. socket.io keeps retrying on its
 * own; nothing that calls this should have to handle a connection error.
 */
export function getSocket(): Socket | null {
  if (socket) return socket;
  try {
    const token = getStoredToken();
    const instance = io(window.location.origin, {
      auth: token ? { token } : {},
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 10000,
    });

    instance.on("connect", () => {
      instance.emit("subscribe-events");
      instance.emit("subscribe-runs");
    });
    // Listened for so a dead backend stays quiet instead of surfacing anywhere.
    instance.on("connect_error", () => undefined);

    socket = instance;
    return socket;
  } catch {
    return null;
  }
}

/**
 * Drops the connection so the next getSocket() re-handshakes. Used when the
 * token changes, since auth is only sent at handshake time.
 */
export function resetSocket(): void {
  const previous = socket;
  socket = null;
  if (previous) {
    try {
      previous.removeAllListeners();
      previous.disconnect();
    } catch {
      /* already gone */
    }
  }
  bumpGeneration();
}

/**
 * Subscribes to a socket event for the life of the component.
 *
 * The handler is read through a ref, so passing an inline arrow does not
 * resubscribe on every render.
 */
export function useSocketEvent<T>(event: string, handler: (payload: T) => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const gen = useGeneration();

  useEffect(() => {
    const instance = getSocket();
    if (!instance) return;

    const listener = (payload: T) => handlerRef.current(payload);
    instance.on(event, listener);
    return () => {
      instance.off(event, listener);
    };
  }, [event, gen]);
}

/** Live connection state, for a status dot in the header. */
export function useSocketConnected(): boolean {
  const [connected, setConnected] = useState(false);
  const gen = useGeneration();

  useEffect(() => {
    const instance = getSocket();
    if (!instance) {
      setConnected(false);
      return;
    }

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    setConnected(instance.connected);
    instance.on("connect", onConnect);
    instance.on("disconnect", onDisconnect);
    return () => {
      instance.off("connect", onConnect);
      instance.off("disconnect", onDisconnect);
    };
  }, [gen]);

  return connected;
}
